import { DateTime, Interval } from "luxon";
import React, { useEffect } from "react";
import { ECDatePicker } from "../molecules/ECDatePicker";
import {
  evaluateTimesFromEndTimeChange,
  evaluateTimesFromStartTimeChange,
  isValidTimeInput,
} from "../utils/eventCardTime.util";

import { ECTimePicker } from "../molecules/ECTimePicker";

const getDisplayTime = (time: DateTime) => {
  return time.toFormat("h:mma");
};

const buildTimeSlots = (startTime: string) => {
  const startDateTime = DateTime.fromFormat(startTime, "h:mma").plus({ minutes: 30 });
  return Interval.fromDateTimes(startDateTime, startDateTime.endOf("day"))
    .splitBy({ minutes: 30 })
    .map((interval) => {
      return interval.start.toFormat("h:mma");
    });
};

export const ECTime = ({
  readOnly,
  startTime,
  endTime,
  onChange,
  isAllDay,
  timeError,
  setTimeError,
}: {
  readOnly: boolean;
  startTime?: DateTime;
  endTime?: DateTime;
  onChange: (start?: DateTime, end?: DateTime) => void;
  isAllDay: boolean;
  timeError: string | null;
  setTimeError: (error: string | null) => void;
}) => {
  const [startDateTime, setStartDateTime] = React.useState<DateTime | undefined>(startTime);
  const [endDateTime, setEndDateTime] = React.useState<DateTime | undefined>(endTime);

  useEffect(() => {
    if (startTime && startDateTime && startTime.toMillis() === startDateTime.toMillis()) {
      return;
    }

    setStartDateTime(startTime);
  }, [startTime?.toMillis()]);

  useEffect(() => {
    if (endTime && endDateTime && endTime.toMillis() === endDateTime.toMillis()) {
      return;
    }

    setEndDateTime(endTime);
  }, [endTime?.toMillis()]);

  const isSameDay = startDateTime && endDateTime && startDateTime.hasSame(endDateTime, "day");
  const onChangeStartDate = (iso: DateTime) => {
    const newStartDateValues = {
      year: iso.year,
      month: iso.month,
      day: iso.day,
    };
    const updatedStartDateTime = startDateTime
      ? startDateTime.set(newStartDateValues)
      : DateTime.fromObject(newStartDateValues).set({ hour: 9 });
    setStartDateTime(updatedStartDateTime);
    onChange(updatedStartDateTime, endDateTime ?? updatedStartDateTime.plus({ minutes: 30 }));
    if (isAllDay) {
      const isSameDay = endDateTime && updatedStartDateTime.hasSame(endDateTime, "day");
      if (isSameDay) {
        setTimeError("All day events must have different start and end dates");
      }
    }
  };

  const onChangeEndDate = (iso: DateTime) => {
    const newEndDateValues = {
      year: iso.year,
      month: iso.month,
      day: iso.day,
    };
    const updatedEndDateTime = endDateTime
      ? endDateTime.set(newEndDateValues)
      : DateTime.fromObject(newEndDateValues);
    setEndDateTime(updatedEndDateTime);
    onChange(startDateTime ?? updatedEndDateTime.minus({ minutes: 30 }), updatedEndDateTime);
    if (isAllDay) {
      const isSameDay = startDateTime && startDateTime.hasSame(updatedEndDateTime, "day");
      if (isSameDay) {
        setTimeError("All day events must have different start and end dates");
      }
    }
  };

  const onChangeStartTime = (newStartTime: string) => {
    if (!isValidTimeInput(newStartTime)) {
      return setTimeError("Invalid start time");
    }
    const { start, end } = evaluateTimesFromStartTimeChange(
      newStartTime,
      startDateTime,
      endDateTime,
    );

    setTimeError(null);
    onChange(start, end);
  };

  const onChangeEndTime = (newEndTime: string) => {
    if (!isValidTimeInput(newEndTime)) {
      return setTimeError("Invalid end time");
    }
    const { start, end } = evaluateTimesFromEndTimeChange(newEndTime, startDateTime, endDateTime);

    setTimeError(null);
    onChange(start, end);
  };

  return (
    <div className="cw-flex cw-flex-col cw-gap-2 cw-flex-1">
      <div className="cw-flex cw-flex-1 cw-items-center cw-gap-2">
        <ECDatePicker
          date={startDateTime ?? null}
          readOnly={readOnly}
          onChange={onChangeStartDate}
          error={timeError}
        />
        {!isAllDay && (
          <ECTimePicker
            value={startDateTime ? getDisplayTime(startDateTime) : null}
            onChange={onChangeStartTime}
            slots={buildTimeSlots("12:00am")}
            readOnly={readOnly}
            error={timeError}
          />
        )}
      </div>

      <div className="cw-flex cw-flex-1 cw-items-center cw-gap-2">
        <ECDatePicker
          date={endDateTime ?? null}
          readOnly={readOnly}
          onChange={onChangeEndDate}
          error={timeError}
        />
        {!isAllDay && (
          <ECTimePicker
            value={endDateTime ? getDisplayTime(endDateTime) : null}
            onChange={onChangeEndTime}
            slots={
              isSameDay && startDateTime
                ? buildTimeSlots(startDateTime.toFormat("h:mma"))
                : buildTimeSlots("12:00am")
            }
            readOnly={readOnly}
            error={timeError}
          />
        )}
      </div>

      {timeError && (
        <div className="cw-text-destructive cw-text-13 cw-font-normal cw-text-center">
          {timeError}
        </div>
      )}
    </div>
  );
};
